import {
	computed,
	observable,
	action,
	reaction,
	IReactionDisposer,
	makeObservable,
} from 'mobx';
import { add, getUnixTime, sub } from 'date-fns';
import {
	FlightEnrichStore,
	FlightStore,
	FlightFilterStore,
	FlightSortStore,
} from '@move-frontend/dap';
import { IAsyncStore } from '@move-frontend/utils';

import {
	IEnrichedFlightProps,
	IEnrichedFlight,
} from '../../../shared/flight/domain/models/IEnrichedFlight';
import { IFlight } from '../../../shared/flight/domain/models/IFlight';
import { FlightListScope } from '../constants/FlightListScope';
import { createFlightSearchFilter } from './createFlightSearchFilter';
import { logError } from '../../../../shared/error/logError';
import { AirportCodes } from '../../../../../types/airports';
import { getFlightTimeForSorting } from '../getFlightTimeForSorting';

export class FlightListStore implements IAsyncStore {
	protected _selectedDay = new Date();

	protected _showFromIndex = 0;

	protected _showToIndex = 0;

	protected _minimumListLength = 15;

	protected _query = '';

	private _scope: FlightListScope = FlightListScope.Departures;

	private _selectedAirportCode: AirportCodes | undefined;

	private disposeErrorReaction: IReactionDisposer;

	public initialLoadFinished = false;

	constructor(
		protected readonly flightStore: FlightStore<IFlight>,
		protected readonly flightEnrichStore: FlightEnrichStore<
			IFlight,
			IEnrichedFlightProps
		>,
		protected readonly flightFilterStore: FlightFilterStore<IEnrichedFlight>,
		protected readonly flightSortStore: FlightSortStore<IEnrichedFlight>,
	) {
		makeObservable<
			FlightListStore,
			| '_scope'
			| '_selectedAirportCode'
			| '_showFromIndex'
			| '_query'
			| '_showToIndex'
			| 'showFromIndex'
			| 'showToIndex'
		>(this, {
			_scope: observable,
			_selectedAirportCode: observable,
			_showFromIndex: observable,
			_showToIndex: observable,
			_query: observable,
			finishedInitialLoad: computed,
			isLoading: computed,
			error: computed,
			enrichedFlights: computed,
			filteredFlights: computed,
			sortedFlights: computed,
			flights: computed,
			totalResults: computed,
			canNavigateBackward: computed,
			canNavigateForward: computed,
			showFromIndex: computed,
			showToIndex: computed,
			query: computed,
			setScope: action.bound,
			setSelectedAirportCode: action.bound,
			loadFlights: action.bound,
			showEarlier: action.bound,
			showLater: action.bound,
			setQuery: action.bound,
			initialLoad: action.bound,
			updateFlightWindow: action.bound,
		});

		this.disposeErrorReaction = reaction(
			() => this.flightStore.error,
			this.onError,
		);

		this.setScope(FlightListScope.Departures);
	}

	public dispose() {
		this.disposeErrorReaction();
	}

	public setSelectedAirportCode(airportCode?: AirportCodes) {
		this._selectedAirportCode = airportCode;

		this.updateFlightWindow();
	}

	public get minimumListLength() {
		return this._minimumListLength;
	}

	public get finishedInitialLoad() {
		return this.flightStore.finishedInitialLoad;
	}

	public get isLoading() {
		return this.flightStore.isLoading;
	}

	public get error() {
		return this.flightStore.error;
	}

	public get enrichedFlights() {
		return this.flightEnrichStore.enrich(
			this.flightStore.flights,
		) as IEnrichedFlight[];
	}

	public get filteredFlights() {
		const filteredFlightsByCode =
			this._selectedAirportCode &&
			this.enrichedFlights.filter((flight) => {
				const airportCode = this._selectedAirportCode?.toLowerCase();
				const baseAirport = flight.baseAirport.toLowerCase();
				const arrivalAirport = flight.arrivalAirport.toLowerCase();
				const departureAirport = flight.departureAirport.toLowerCase();

				return (
					baseAirport === airportCode ||
					arrivalAirport === airportCode ||
					departureAirport === airportCode
				);
			});

		return this.flightFilterStore.filter(
			filteredFlightsByCode || this.enrichedFlights,
		);
	}

	public get sortedFlights() {
		return this.flightSortStore.sort(this.filteredFlights);
	}

	public get flights() {
		return this.sortedFlights.slice(this.showFromIndex, this.showToIndex);
	}

	public get totalResults() {
		return this.sortedFlights.length;
	}

	public get canNavigateBackward() {
		return this._showFromIndex > 0;
	}

	public get canNavigateForward() {
		const newIndex = this._showToIndex + this.minimumListLength;
		return newIndex < this.totalResults;
	}

	protected get showFromIndex() {
		return this._showFromIndex;
	}

	protected get showToIndex() {
		return this._showToIndex;
	}

	public get scope() {
		return this._scope;
	}

	public setScope(scope: FlightListScope) {
		this._scope = scope;

		switch (scope) {
			case FlightListScope.Arrivals:
				this.flightFilterStore.setFieldFilter('arrival', true);
				break;
			case FlightListScope.Departures:
				this.flightFilterStore.setFieldFilter('arrival', false);
				break;
			default:
				throw new Error(`Unknown scope: ${scope}`);
		}

		this.updateFlightWindow();
	}

	public async loadFlights() {
		const now = new Date();

		const from = sub(now, { hours: 12 });
		const to = add(now, { hours: 24 });

		await this.flightStore.loadBetween(getUnixTime(from), getUnixTime(to));

		this.updateFlightWindow();
	}

	public updateFlightWindow() {
		const fromIndex = this.findIndexOfFirstFlightScheduledAfter(
			getUnixTime(new Date()),
		);

		this._showFromIndex = fromIndex;
		this._showToIndex = fromIndex + this.minimumListLength;
	}

	protected findIndexOfFirstFlightScheduledAfter(timestamp: number) {
		return this.sortedFlights.findIndex(
			(flight) => getFlightTimeForSorting(flight) >= timestamp,
		);
	}

	public async initialLoad() {
		await this.loadFlights();
		return { totalResults: this.totalResults };
	}

	public showEarlier() {
		this._showFromIndex = Math.max(
			0,
			this._showFromIndex - this.minimumListLength,
		);
	}

	public showLater() {
		this._showToIndex = Math.min(
			this.totalResults,
			this._showToIndex + this.minimumListLength,
		);
	}

	public get query() {
		return this._query;
	}

	public setQuery(query: string) {
		this._showFromIndex = 0;
		const trimmedQuery = query.trim();
		this._query = trimmedQuery;

		if (trimmedQuery === '') {
			this.flightFilterStore.removeFilter('query-filter');
		} else {
			const queryFilter = createFlightSearchFilter(trimmedQuery);
			this.flightFilterStore.setFilter('query-filter', queryFilter);
		}
		this.updateFlightWindow();
	}

	private onError = () => {
		if (this.error) {
			logError(this.error, {});
		}
	};
}
